class Scroller {
  constructor(container, options = {}) {
    const defaults = {
      scrollSpeed: 500, // 每秒滚动像素数
      transitionSpeed: 2500, // 缓动动画时长（毫秒）
      initialSlide: 0, // 初始显示条目索引（从 0 开始）
      itemSelector: "li",
      directionUp: true, // 初始滚动方向
      onEnd: () => {}, // 滚动结束时的回调函数
    };
    this.options = Object.assign({}, defaults, options);
    this.container = container;
    if (!this.container) {
      throw new Error(`元素不存在: ${selector}`);
    }
    this.init();
  }

  init() {
    // 复制列表内容以实现无缝滚动
    this.container.innerHTML += this.container.innerHTML;
    // 获取第一个列表项，计算高度（采用 getBoundingClientRect 并取整）
    const item = this.container.querySelector(this.options.itemSelector);
    this.itemHeight = Math.round(item.getBoundingClientRect().height);
    // 原始项数（复制后需除以 2）
    const nodes = this.container.querySelectorAll(this.options.itemSelector);
    this.originalCount = nodes.length / 2;
    this.originalTotal = this.itemHeight * this.originalCount;
    // 初始化连续 offset 与状态
    this.offset = this.options.initialSlide * this.itemHeight;
    // 定义：offset 表示列表向上滚动的累计距离
    // 向上滚动时 offset 递增，向下滚动时 offset 递减（可能为负）
    this.directionUp = this.options.directionUp; // 默认向上滚动
    this.running = false;
    this.animationId = null;
    this.lastTime = null;
    this.setTranslate(this.normalizeOffset(this.offset));
  }

  // 将任意 offset 归一化到 [0, originalTotal) 内（用于显示）
  normalizeOffset(v) {
    const r = Math.round(v);
    return ((r % this.originalTotal) + this.originalTotal) % this.originalTotal;
  }

  // 设置 transform 显示（使用归一化后的 offset）
  setTranslate(o) {
    this.container.style.transform = `translateY(-${o}px)`;
  }

  // 连续滚动动画，利用 requestAnimationFrame 更新 offset
  animateScroll(timestamp) {
    if (!this.lastTime) this.lastTime = timestamp;
    const delta = (timestamp - this.lastTime) / 1000; // 秒
    this.lastTime = timestamp;
    if (this.directionUp) {
      this.offset += this.options.scrollSpeed * delta;
    } else {
      this.offset -= this.options.scrollSpeed * delta;
    }
    this.setTranslate(this.normalizeOffset(this.offset));
    this.animationId = requestAnimationFrame((ts) => this.animateScroll(ts));
  }

  // 启动连续滚动
  start() {
    if (!this.running) {
      this.running = true;
      this.lastTime = null;
      this.animationId = requestAnimationFrame((ts) => this.animateScroll(ts));
    }
  }

  // 计算候选目标：保证候选值 candidate = base + (stopSlideIndex * itemHeight)
  // 对于向上滚动，candidate 必须大于当前 offset；对于向下滚动，candidate 必须小于当前 offset
  computeCandidateContinuous(currentOffset, targetBase, goingUp) {
    const norm = this.normalizeOffset(currentOffset);
    const base = currentOffset - norm;
    let candidate = base + targetBase;
    if (goingUp) {
      if (candidate <= currentOffset) {
        candidate += this.originalTotal;
      }
    } else {
      if (candidate >= currentOffset) {
        candidate -= this.originalTotal;
      }
    }
    return candidate;
  }

  // 缓动函数 easeOutQuad
  easeOutQuad(t) {
    return t * (2 - t);
  }

  // JS 缓动动画：从当前 offset 缓动到 target（连续值），duration 毫秒
  animateToTarget(target, duration, callback) {
    const startTime = performance.now();
    const startOffset = this.offset;
    const step = (now) => {
      const elapsed = now - startTime;
      const t = Math.min(elapsed / duration, 1);
      const eased = this.easeOutQuad(t);
      const newOffset = startOffset + (target - startOffset) * eased;
      this.setTranslate(this.normalizeOffset(newOffset));
      if (t < 1) {
        requestAnimationFrame(step);
      } else {
        this.offset = target;
        this.setTranslate(this.normalizeOffset(this.offset));
        if (callback) callback();
      }
    };
    requestAnimationFrame(step);
  }

  // 停止滚动：取消连续动画后，计算候选目标并用 JS 缓动动画顺向校准
  stop(stopSlideIndex) {
    if (this.running) {
      this.running = false;
      cancelAnimationFrame(this.animationId);
      const targetBase = stopSlideIndex * this.itemHeight;
      const candidateValue = this.computeCandidateContinuous(
        this.offset,
        targetBase,
        this.directionUp,
      );
      this.animateToTarget(candidateValue, this.options.transitionSpeed, () => {
        this.offset = candidateValue;
        this.options?.onEnd?.(stopSlideIndex);
      });
    }
  }

  // 切换滚动方向：停止当前动画，切换方向后重新启动
  toggleDirection() {
    if (this.running) {
      cancelAnimationFrame(this.animationId);
      this.running = false;
    }
    this.directionUp = !this.directionUp;
    this.start();
  }
}

export default Scroller;

// 使用示例
// const scroller = new VerticalSeamlessScroller("#container", {
//   scrollSpeed: 500,
//   transitionSpeed: 2500,
//   initialSlide: 0,
//   stopSlideIndex: 2,
//   itemSelector: "li",
// });

// document
//   .getElementById("startBtn")
//   .addEventListener("click", () => scroller.start());
// document
//   .getElementById("stopBtn")
//   .addEventListener("click", () => scroller.stop());
// document
//   .getElementById("toggleDirBtn")
//   .addEventListener("click", () => scroller.toggleDirection());
